/**
 * PendingApprovalsSection — DEC-329: "Waiting for your approval" on the Stuck jobs page, for
 * the managers who decide dispatcher-completion requests (COMPLETION_APPROVER_ROLES).
 *
 *   Approve → POST /v1/dispatcher-completion-requests/:id/approve {}: the job goes to In Review
 *             (the requester's force, approved by this manager).
 *   Decline → POST …/:id/decline { reason ≥ 8 }: the job stays on the stuck list.
 *
 * The API refuses a request whose job is no longer stuck (409 STALE, and it is then cancelled)
 * or one somebody else already decided; the toast shows the API's own sentence, which is why
 * this uses authedFetch rather than useApiMutation (that keeps only the code, not the message).
 * Every outcome refreshes ['jobs'] (the stuck list and these requests) and ['inreview'].
 * Times render in the manager's own timezone (DEC-004 / DEC-066).
 */

import * as React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { authedFetch, useApiOpts } from '@lodex-pro/web-shared';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { formatCreated } from '../../lib/timestamp';
import type { CompletionRequestRow } from './completion-policy';

const DECLINE_REASON_MIN = 8;
const GENERIC_FAILURE = 'Could not save the decision. Try again.';

async function readError(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as {
      error?: { message?: unknown; fields?: ReadonlyArray<{ reason?: unknown }> };
    };
    const reason = body.error?.fields?.[0]?.reason;
    if (typeof reason === 'string') return reason;
    const message = body.error?.message;
    return typeof message === 'string' ? message : GENERIC_FAILURE;
  } catch {
    return GENERIC_FAILURE;
  }
}

export interface PendingApprovalsSectionProps {
  readonly requests: readonly CompletionRequestRow[];
  readonly timeZone: string;
}

const sectionStyle: React.CSSProperties = {
  padding: '12px 16px',
  borderBottom: '1px solid var(--color-border-primary)',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: '12px',
  padding: '8px 10px',
  border: '1px solid var(--color-border-secondary)',
  borderRadius: '6px',
  fontSize: '13px',
};

const mutedStyle: React.CSSProperties = { fontSize: '12px', color: 'var(--color-text-tertiary)' };

export function PendingApprovalsSection({
  requests,
  timeZone,
}: PendingApprovalsSectionProps): React.JSX.Element | null {
  const apiOpts = useApiOpts();
  const queryClient = useQueryClient();
  const [declining, setDeclining] = React.useState<CompletionRequestRow | null>(null);
  const [declineReason, setDeclineReason] = React.useState('');

  const decide = useMutation<
    void,
    Error,
    { id: string; action: 'approve' | 'decline'; payload: Record<string, unknown> }
  >({
    mutationFn: async ({ id, action, payload }) => {
      const res = await authedFetch(apiOpts, `/v1/dispatcher-completion-requests/${id}/${action}`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await readError(res));
    },
    onSuccess: (_v, { action }) => {
      toast.success(action === 'approve' ? 'Approved — sent to review' : 'Declined');
      setDeclining(null);
    },
    onError: (err) => {
      toast.error(err.message);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['jobs'] });
      void queryClient.invalidateQueries({ queryKey: ['inreview'] });
    },
  });

  if (requests.length === 0) return null;
  const declineValid = declineReason.trim().length >= DECLINE_REASON_MIN;

  return (
    <section style={sectionStyle} data-testid="pending-approvals">
      <div style={{ fontSize: '13px', fontWeight: 700 }}>
        Waiting for your approval ({requests.length.toString()})
      </div>
      {requests.map((r) => (
        <div key={r.id} style={rowStyle} data-testid={`pending-approval-${r.id}`}>
          <div style={{ flex: '1 1 260px' }}>
            <div>
              <strong>{r.haulId ?? 'Job'}</strong> · {r.driverName ?? '—'} · {r.siteName ?? '—'}
            </div>
            <div style={mutedStyle}>
              Asked by {r.requestedByName ?? 'a dispatcher'} ·{' '}
              {formatCreated(r.requestedAt, timeZone)}
            </div>
            <div>Reason: {r.reason}</div>
          </div>
          <Button
            type="button"
            disabled={decide.isPending}
            onClick={(): void => decide.mutate({ id: r.id, action: 'approve', payload: {} })}
          >
            Approve
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={decide.isPending}
            onClick={(): void => {
              setDeclineReason('');
              setDeclining(r);
            }}
          >
            Decline
          </Button>
        </div>
      ))}

      <Dialog
        open={declining !== null}
        onOpenChange={(next): void => {
          if (!next) setDeclining(null);
        }}
      >
        <DialogContent className="w-[calc(100vw-2rem)] max-w-md">
          <DialogHeader>
            <DialogTitle>{`Decline — ${declining?.haulId ?? 'job'}`}</DialogTitle>
          </DialogHeader>
          <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', margin: 0 }}>
            The job stays on the stuck list. Your reason is shown to the dispatchers.
          </p>
          <textarea
            value={declineReason}
            onChange={(e): void => setDeclineReason(e.target.value)}
            placeholder="Why not? (min 8 characters)"
            aria-label="Decline reason"
            style={{
              width: '100%',
              minHeight: '80px',
              padding: '8px 10px',
              fontSize: '14px',
              backgroundColor: 'var(--color-bg-tertiary)',
              border: '1px solid var(--color-border-secondary)',
              borderRadius: '10px',
              color: 'var(--color-text-primary)',
            }}
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={(): void => setDeclining(null)}>
              Back
            </Button>
            <Button
              type="button"
              disabled={!declineValid || decide.isPending || declining === null}
              onClick={(): void => {
                if (declining === null || !declineValid) return;
                decide.mutate({
                  id: declining.id,
                  action: 'decline',
                  payload: { reason: declineReason.trim() },
                });
              }}
            >
              Decline
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
